import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PageForm from "components/pageForm/PageForm";
import SubNavbar from "components/SubNavbar/SubNavbar";
import ApiDatabase from 'server';
import Usefetchdata from "hooks/usefetchdata";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import Chip from "components/chip/chip";
import Button from "components/button";
import ThreeStateCheckbox from "components/threeStateCheckbox/threeStateCheckbox";
import { Loading2, BanIcon, CheckIcon } from "assets/icons";
import { ADMIN_ROLE_LABELS, SUPER_ADMIN_ROLE } from "helper/roles";

const MISSIONS_ROLE = 525;

const roleColors = {
    500: 'WARNING',
    510: 'PRIMARY',
    515: 'PRIMARY',
    520: 'INFO',
};

// Every admin role but 500, which the api never lets anyone give.
const ASSIGNABLE_ROLES = [
    ...new Set([
        ...Object.keys(ADMIN_ROLE_LABELS).map(Number),
        MISSIONS_ROLE,
    ]),
]
    .filter((role) => role !== SUPER_ADMIN_ROLE)
    .sort((a, b) => a - b);

// The api's own messages are in English: only its errorCode is shown, translated.
const ROLE_ERROR_KEYS = {
    INVALID_ID: 'memberNotFound',
    ADMIN_MEMBER_NOT_FOUND: 'memberNotFound',
    UNKNOWN_ADMIN_ROLE: 'unknownRole',
    NOT_AN_ADMIN: 'notAnAdmin',
    ADMIN_MEMBER_DEACTIVATED: 'memberDeactivated',
    CANNOT_CHANGE_OWN_ROLE: 'ownRole',
    CANNOT_CHANGE_SUPER_ADMIN: 'superAdmin',
};

// The member changed since the list was loaded: reload it rather than leave a stale row.
const STALE_MEMBER_ERROR_CODES = [
    'INVALID_ID',
    'ADMIN_MEMBER_NOT_FOUND',
    'NOT_AN_ADMIN',
    'ADMIN_MEMBER_DEACTIVATED',
    'CANNOT_CHANGE_SUPER_ADMIN',
];

// Total + one card per role: as many columns as cards on desktop, and on
// tablet a split that never leaves a card alone on its row.
const COUNTER_GRID_COLUMNS = {
    3: 'tab:grid-cols-3',
    4: 'tab:grid-cols-2 lg:grid-cols-4',
    5: 'tab:grid-cols-3 lg:grid-cols-5',
    6: 'tab:grid-cols-3 lg:grid-cols-6',
};

const getInitials = (firstname, lastname) => {
    const f = (firstname || '').charAt(0).toUpperCase();
    const l = (lastname || '').charAt(0).toUpperCase();
    return (f + l) || '--';
};

const AdminConsole = () => {
    const { userData } = Usefetchdata();
    const { setModalData, modalDataReturn, language } = UseContext();
    const { toggle } = useModal();
    const [team, setTeam] = useState([]);
    const [deactivatedMembers, setDeactivatedMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingRoleOf, setUpdatingRoleOf] = useState(null);
    // false = n'affiche pas les désactivés (par défaut), true = affiche seulement désactivés, null = affiche tout
    const [showDeactivated, setShowDeactivated] = useState(false);
    const token = localStorage.getItem('xsrfToken');

    const fetchTeam = () => {
        if (token) {
            ApiDatabase.getAdminTeam(
                { token },
                (data) => {
                    setTeam(data);
                    setLoading(false);
                },
                (err) => {
                    console.error(err);
                    setLoading(false);
                }
            );
        }
    };

    const fetchDeactivatedMembers = () => {
        if (token) {
            ApiDatabase.getDeactivatedAdminTeam(
                { token },
                (data) => {
                    setDeactivatedMembers(data);
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    };

    useEffect(() => {
        fetchTeam();
        fetchDeactivatedMembers();
    }, [token]);

    useEffect(() => {
        if (modalDataReturn?.action === 'admin_member_deactivated' || modalDataReturn?.action === 'admin_member_reactivated') {
            fetchTeam();
            fetchDeactivatedMembers();
        }
    }, [modalDataReturn]);

    const handleDeactivate = (member) => {
        setModalData({ ...member, mode: 'deactivate' });
        toggle('admin_console_deactivate');
    };

    const handleReactivate = (member) => {
        setModalData({ ...member, mode: 'reactivate' });
        toggle('admin_console_reactivate');
    };

    const consoleText = (key) => language.getValue(`adminConsole.${key}`, '');

    const getRoleLabel = (role) =>
        ADMIN_ROLE_LABELS[role] ||
        (role === MISSIONS_ROLE ? consoleText('roleMissions') : `Rôle ${role}`);

    const handleRoleChange = (member, role) => {
        if (role === member.role) return;
        setUpdatingRoleOf(member._id);
        ApiDatabase.updateAdminTeamMemberRole(
            { id_user: member._id, role },
            () => {
                setUpdatingRoleOf(null);
                toast.success(consoleText('roleUpdated'));
                fetchTeam();
            },
            (err) => {
                setUpdatingRoleOf(null);
                const errorCode = err?.response?.data?.errorCode;
                toast.error(consoleText(`roleErrors.${ROLE_ERROR_KEYS[errorCode] || 'generic'}`));
                if (STALE_MEMBER_ERROR_CODES.includes(errorCode)) {
                    fetchTeam();
                    fetchDeactivatedMembers();
                }
            }
        );
    };

    // Détermine quels membres afficher selon le filtre
    const getDisplayMembers = () => {
        if (showDeactivated === true) {
            return deactivatedMembers;
        } else if (showDeactivated === false) {
            return team;
        } else {
            // null = affiche tout
            return [...team, ...deactivatedMembers];
        }
    };

    const displayMembers = getDisplayMembers();
    const totalMembers = team.length;
    const countByRole = (role) => team.filter(m => m.role === role).length;
    const deactivatedIds = new Set(deactivatedMembers.map((m) => m._id));
    // 525 is not in ADMIN_ROLE_LABELS (its label is translated), yet it gets
    // its own card so the role cards add up to the total.
    const roleCounters = [
        ...Object.entries(ADMIN_ROLE_LABELS).map(([role, label]) => [Number(role), label]),
        [MISSIONS_ROLE, consoleText('roleMissions')],
    ];
    const counterCardCount = roleCounters.length + 1;

    return (
        <PageForm>
            <SubNavbar>
                <div className="flex justify-between items-center w-full gap-3">
                    <div className="flex gap-2 items-center">
                        {/* Checkbox pour afficher les membres désactivés */}
                        <div className="border rounded px-4 h-10 flex items-center hover:bg-gray-100 transition-colors">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <ThreeStateCheckbox
                                    checked={showDeactivated}
                                    onChange={setShowDeactivated}
                                />
                                <div className="whitespace-nowrap text-sm">Membres désactivés</div>
                            </label>
                        </div>
                    </div>
                </div>
            </SubNavbar>
            <div className="w-full px-2 tab:px-4 lg:px-8 py-8 mb-28 flex flex-col gap-8">

                <div className={`grid grid-cols-1 ${COUNTER_GRID_COLUMNS[counterCardCount] || 'tab:grid-cols-3'} gap-4 w-full`}>
                    <div className="bg-gray-50 border border-gray-200 flex flex-col items-start rounded-lg px-6 py-5">
                        <p className="text-sm font-medium text-gray-700">Total membres</p>
                        <p className="text-3xl font-medium text-gray-900">{loading ? '-' : totalMembers}</p>
                    </div>
                    {roleCounters.map(([role, label]) => (
                        <div key={role} className="bg-gray-50 border border-gray-200 flex flex-col items-start rounded-lg px-6 py-5">
                            <p className="text-sm font-medium text-gray-700">{label}</p>
                            <p className="text-3xl font-medium text-gray-900">{loading ? '-' : countByRole(role)}</p>
                        </div>
                    ))}
                </div>

                <div className="w-full bg-white border border-gray-100 rounded-lg overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loading2 wh={32} color="#9CA3AF" />
                        </div>
                    ) : displayMembers.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <p className="text-sm text-gray-500">Aucun membre trouvé</p>
                        </div>
                    ) : (
                        <table className="table-auto w-full text-gray-500 ft-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="py-3.5 pl-4 pr-2 text-left text-sm font-medium text-gray-700 bg-gray-50">MEMBRE</th>
                                    <th className="py-3.5 px-2 text-left text-sm font-medium text-gray-700 bg-gray-50">RÔLE</th>
                                    <th className="py-3.5 px-2 pr-4 text-right text-sm font-medium text-gray-700 bg-gray-50"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayMembers.map((member, index) => {
                                    const isDeactivated = member.firstname?.includes('*') || deactivatedIds.has(member._id);
                                    const isCurrentUser = userData?.email === member.email;
                                    const canChangeRole = !isCurrentUser && !isDeactivated && member.role !== SUPER_ADMIN_ROLE;
                                    return (
                                        <tr key={member._id} className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 motion-safe:animate-fadeUp" style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}>
                                            <td className="py-4 pl-4 pr-2">
                                                <div className="flex items-center gap-4">
                                                    <div className="min-w-[40px] w-[40px] h-[40px] rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {getInitials(member.firstname, member.lastname)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-col items-start justify-center min-w-0 flex-1">
                                                        <p className="text-gray-900">
                                                            {member.firstname} {member.lastname}
                                                            {isCurrentUser && <span className="text-gray-400 ml-1">(vous)</span>}
                                                        </p>
                                                        <p className="text-gray-500 text-xs">{member.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-2">
                                                {canChangeRole ? (
                                                    <select
                                                        aria-label={consoleText('roleSelectLabel').replace('{name}', `${member.firstname || ''} ${member.lastname || ''}`.trim())}
                                                        value={member.role}
                                                        disabled={updatingRoleOf === member._id}
                                                        onChange={(e) => handleRoleChange(member, Number(e.target.value))}
                                                        className="h-8 pl-2 pr-3 border rounded-md bg-white text-sm text-gray-900 focus:border-yellow-500 focus:outline-none disabled:opacity-50"
                                                    >
                                                        {!ASSIGNABLE_ROLES.includes(member.role) && (
                                                            <option value={member.role} disabled>{getRoleLabel(member.role)}</option>
                                                        )}
                                                        {ASSIGNABLE_ROLES.map((role) => (
                                                            <option key={role} value={role}>{getRoleLabel(role)}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <Chip type="INDICATOR" color={roleColors[member.role] || 'PRIMARY'}>
                                                        {getRoleLabel(member.role)}
                                                    </Chip>
                                                )}
                                            </td>
                                            <td className="py-4 px-2 pr-4">
                                                <div className="flex justify-end items-center w-full">
                                                    {!isCurrentUser && !isDeactivated && member.role !== 500 && (
                                                        <Button
                                                            size="ICON"
                                                            color="SECONDARY"
                                                            onClick={() => handleDeactivate(member)}
                                                            className="border border-gray-200 ml-auto"
                                                        >
                                                            <BanIcon wh={18} color="#DC2626" />
                                                        </Button>
                                                    )}
                                                    {!isCurrentUser && isDeactivated && (
                                                        <Button
                                                            size="ICON"
                                                            color="SECONDARY"
                                                            onClick={() => handleReactivate(member)}
                                                            className="border border-gray-200 ml-auto"
                                                        >
                                                            <CheckIcon wh={18} color="#16A34A" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </PageForm>
    );
};

export default AdminConsole;
